import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { X, Mic, Square, RotateCcw, Play } from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import useChat from '../../hooks/useChat';
import styles from './VoiceSettingsModal.module.css';

const PROVIDER_OPTIONS = [
  { value: 'anthropic', label: 'Claude (Anthropic)' },
  { value: 'openai', label: 'GPT (OpenAI)' },
];

const API_URL = import.meta.env.VITE_CHAT_API_URL || 'http://localhost:3001';

const READING_PASSAGE =
  `The art of conversation is not about filling every silence, but about knowing ` +
  `when to speak and when to listen. A great idea often begins as a quiet thought, ` +
  `turning over in the mind like a stone smoothed by water. It gathers shape slowly, ` +
  `through trial and error, through the willingness to be wrong before being right. ` +
  `The best tools are the ones that disappear into the work itself, becoming extensions ` +
  `of the hand and eye. They do not demand attention; they simply make the difficult ` +
  `feel effortless. In this way, technology at its finest is invisible — not because ` +
  `it hides, but because it fits so naturally into the rhythm of what we already do. ` +
  `Every creative act is a small rebellion against the way things are, a declaration ` +
  `that something better is possible. The courage to begin is often the hardest part.`;

const MIN_RECORDING_SECONDS = 15;
const MAX_RECORDING_SECONDS = 120;

export default function VoiceSettingsModal({ onClose }) {
  const { session } = useAuth();
  const { provider, setProvider } = useChat();
  const [clone, setClone] = useState(null); // single clone or null
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadStage, setUploadStage] = useState(''); // '' | 'uploading' | 'processing' | 'activating'

  // Recording state
  const [recState, setRecState] = useState('idle'); // idle | recording | review
  const [elapsed, setElapsed] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);

  // Preview playback state
  const [previewLoading, setPreviewLoading] = useState(null); // 'default' | 'clone' | null
  const [previewPlaying, setPreviewPlaying] = useState(null); // 'default' | 'clone' | null
  const previewAudioRef = useRef(null);
  const previewBlobUrlRef = useRef(null);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const token = session?.access_token;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      if (previewAudioRef.current) previewAudioRef.current.pause();
      if (previewBlobUrlRef.current) URL.revokeObjectURL(previewBlobUrlRef.current);
    };
  }, [audioUrl]);

  const fetchClone = async () => {
    try {
      const res = await fetch(`${API_URL}/api/voice/clones`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const clones = data.clones || [];
      setClone(clones[0] || null); // only care about the first (single) clone
    } catch {
      toast.error('Failed to load voice profiles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClone();
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;
      chunksRef.current = [];

      const recorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm',
      });

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
        setRecState('review');
        stream.getTracks().forEach(t => t.stop());
      };

      recorderRef.current = recorder;
      recorder.start(250); // collect chunks every 250ms
      setRecState('recording');
      setElapsed(0);

      timerRef.current = setInterval(() => {
        setElapsed(prev => {
          const next = prev + 1;
          if (next >= MAX_RECORDING_SECONDS) {
            recorder.stop();
            clearInterval(timerRef.current);
          }
          return next;
        });
      }, 1000);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Microphone permission denied');
      } else {
        toast.error('Could not access microphone');
      }
    }
  }, []);

  const stopRecording = useCallback(() => {
    clearInterval(timerRef.current);
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
  }, []);

  const resetRecording = useCallback(() => {
    // Don't revoke blob URL here — the useEffect cleanup handles it
    // after React removes the <audio> element from the DOM.
    // Revoking synchronously before re-render causes ERR_FILE_NOT_FOUND
    // as the still-mounted <audio> retries the dead blob URL.
    setAudioBlob(null);
    setAudioUrl(null);
    setRecState('idle');
    setElapsed(0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!audioBlob) return;

    setUploading(true);
    setUploadStage('uploading');
    try {
      // Delete existing clone first (auto-replace)
      if (clone) {
        await fetch(`${API_URL}/api/voice/clones/${clone.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      const form = new FormData();
      form.append('name', 'My voice');
      form.append('consent', 'true');
      form.append('sample', audioBlob, `voice-clone-${Date.now()}.webm`);

      setUploadStage('processing');
      const res = await fetch(`${API_URL}/api/voice/clones`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Clone failed');
      }

      const { clone: newClone } = await res.json();

      // Auto-activate the new clone
      setUploadStage('activating');
      await fetch(`${API_URL}/api/voice/select`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ profileId: newClone.id }),
      });

      toast.success("Your ego's voice is ready");
      resetRecording();
      await fetchClone();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      setUploadStage('');
    }
  };

  const handleSelect = async (profileId) => {
    try {
      await fetch(`${API_URL}/api/voice/select`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ profileId }),
      });
      fetchClone();
    } catch {
      toast.error('Failed to set active voice');
    }
  };

  const handlePreview = async (voiceId, which, e) => {
    e.stopPropagation(); // don't trigger row selection

    // Stop any currently playing preview
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    if (previewBlobUrlRef.current) {
      URL.revokeObjectURL(previewBlobUrlRef.current);
      previewBlobUrlRef.current = null;
    }

    // If same voice was playing, just stop it
    if (previewPlaying === which) {
      setPreviewPlaying(null);
      return;
    }

    setPreviewPlaying(null);
    setPreviewLoading(which);

    try {
      const res = await fetch(`${API_URL}/api/voice/preview`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ voiceId: voiceId || null }),
      });

      if (!res.ok) {
        throw new Error(res.status === 429 ? 'rate_limited' : 'Preview failed');
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      previewBlobUrlRef.current = url;

      const audio = new Audio(url);
      previewAudioRef.current = audio;

      audio.addEventListener('ended', () => {
        setPreviewPlaying(null);
        previewAudioRef.current = null;
        URL.revokeObjectURL(url);
        previewBlobUrlRef.current = null;
      });

      await audio.play();
      setPreviewPlaying(which);
    } catch (err) {
      toast.error(
        err.message === 'rate_limited'
          ? 'Too many requests — wait a few seconds'
          : 'Could not generate preview'
      );
    } finally {
      setPreviewLoading(null);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.dialog}>
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close"><X size={16} /></button>
        <h2 className={styles.heading}>Settings</h2>

        {/* Model provider */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Model Provider</div>
          <div role="radiogroup" aria-label="Model provider">
            {PROVIDER_OPTIONS.map(opt => (
              <label
                key={opt.value}
                className={`${styles.profileRow} ${provider === opt.value ? styles.profileRowActive : ''}`}
              >
                <input
                  type="radio"
                  name="llm-provider"
                  value={opt.value}
                  checked={provider === opt.value}
                  onChange={() => setProvider(opt.value)}
                  className={styles.providerRadio}
                />
                <span className={styles.providerName}>{opt.label}</span>
                {provider === opt.value && <span className={styles.activeBadge}>Active</span>}
              </label>
            ))}
          </div>
          <div className={styles.providerHint}>
            Applies to new conversations — existing conversations keep their model.
          </div>
        </div>

        {/* Voice toggle */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Active Voice</div>
          {loading ? (
            <div className={styles.empty}>Loading...</div>
          ) : (
            <>
              <div
                className={`${styles.profileRow} ${!clone?.is_active ? styles.profileRowActive : ''}`}
                onClick={() => handleSelect(null)}
              >
                <div className={styles.profileName}>Default voice</div>
                <button
                  className={styles.previewBtn}
                  onClick={(e) => handlePreview(null, 'default', e)}
                  disabled={previewLoading === 'default'}
                  aria-label="Preview default voice"
                >
                  {previewLoading === 'default' ? (
                    <span className={styles.previewBtnSpinner} />
                  ) : previewPlaying === 'default' ? (
                    <Square size={12} />
                  ) : (
                    <Play size={12} />
                  )}
                </button>
                {!clone?.is_active && <span className={styles.activeBadge}>Active</span>}
              </div>
              {clone && (
                <div
                  className={`${styles.profileRow} ${clone.is_active ? styles.profileRowActive : ''}`}
                  onClick={() => handleSelect(clone.id)}
                >
                  <div className={styles.profileName}>My voice</div>
                  <button
                    className={styles.previewBtn}
                    onClick={(e) => handlePreview(clone.provider_voice_id, 'clone', e)}
                    disabled={previewLoading === 'clone'}
                    aria-label="Preview cloned voice"
                  >
                    {previewLoading === 'clone' ? (
                      <span className={styles.previewBtnSpinner} />
                    ) : previewPlaying === 'clone' ? (
                      <Square size={12} />
                    ) : (
                      <Play size={12} />
                    )}
                  </button>
                  {clone.is_active && <span className={styles.activeBadge}>Active</span>}
                </div>
              )}
            </>
          )}
        </div>

        {/* Record-to-clone form */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>{clone ? 'Re-clone Your Voice' : 'Clone Your Voice'}</div>
          <form onSubmit={handleSubmit} className={styles.form}>
            {/* Reading passage */}
            <div className={styles.passageSection}>
              <div className={styles.passageLabel}>
                Read the following passage aloud:
              </div>
              <div className={styles.passage}>{READING_PASSAGE}</div>
            </div>

            {/* Recording controls */}
            <div className={styles.recorderArea}>
              {recState === 'idle' && (
                <button
                  type="button"
                  className={styles.recordBtn}
                  onClick={startRecording}
                >
                  <Mic size={16} />
                  Start recording
                </button>
              )}

              {recState === 'recording' && (
                <div className={styles.recordingRow}>
                  <div className={styles.recordingIndicator}>
                    <span className={styles.recordingDot} />
                    Recording {formatTime(elapsed)}
                  </div>
                  <button
                    type="button"
                    className={`${styles.recordBtn} ${styles.stopBtn}`}
                    onClick={stopRecording}
                    disabled={elapsed < MIN_RECORDING_SECONDS}
                  >
                    <Square size={14} />
                    {elapsed < MIN_RECORDING_SECONDS
                      ? `Wait ${MIN_RECORDING_SECONDS - elapsed}s`
                      : 'Stop'}
                  </button>
                </div>
              )}

              {recState === 'review' && (
                <div className={styles.reviewRow}>
                  <audio src={audioUrl} controls className={styles.audioPreview} />
                  <button
                    type="button"
                    className={styles.reRecordBtn}
                    onClick={resetRecording}
                  >
                    <RotateCcw size={13} />
                    Re-record
                  </button>
                </div>
              )}
            </div>

            <button
              type="submit"
              className={styles.submitBtn}
              disabled={uploading || !audioBlob}
            >
              {uploading ? (
                <span className={styles.submitProgress}>
                  <span className={styles.spinner} />
                  {uploadStage === 'uploading' && 'Uploading sample...'}
                  {uploadStage === 'processing' && 'Processing your voice...'}
                  {uploadStage === 'activating' && 'Activating clone...'}
                </span>
              ) : clone ? 'Re-clone voice' : 'Create clone'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
